import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { ADMIN_HOME, safeInternalPath } from "../auth/redirectTarget";
import { useMfaGate } from "../auth/mfa";
import { useTurnstile } from "../auth/useTurnstile";
import { BoxState } from "../ui/BoxState";
import { Callout } from "../ui/Callout";
import { AuthFrame } from "./auth/AuthFrame";
import { PasswordField } from "./auth/PasswordField";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "../lib/contact";
import {
  classifySignInFailure,
  signInFailureMessage,
  type SignInFailureKind
} from "../auth/signInFailure";

/**
 * Sign-in. architecture.md section 4: email and password, no public registration, and Turnstile on
 * the authentication surfaces. That phrase used to mean this form and password recovery. The same
 * section later measured `grant_type=password` against staging and found the captcha applies to the
 * endpoint rather than to the first sign-in of a session, so any surface that presents a password
 * is one of them. Account settings is now the third.
 *
 * Every failure shows the same sentence. plan.md section 8 requires account-enumeration-safe
 * responses, so the interface must not distinguish "no such account" from "wrong password" — and
 * that is a property of this file, because Supabase returns different errors underneath.
 *
 * The password is not always the whole of signing in. architecture.md section 4 requires `aal2` for
 * an admin, so a correct password can leave a session that is authenticated and still not admitted
 * anywhere. Where a session goes next is therefore `useMfaGate`'s answer rather than a fixed
 * destination: an account with a verified factor is sent to `/verify`, and an account without
 * one goes where it always went, having seen no second step at all.
 *
 * **Until 2026-09-08 it threw away where the person was going.** `RequireSession` has always sent
 * the intended path here as router state, and this screen never read it, so every sign-in landed on
 * `/overview` or `/admin/requests`. A client who tapped a notification about REQ-142, was redirected
 * because the session had expired, and signed in, arrived on the overview with no request and
 * nothing naming which one had been mentioned. `VerificationRoute` line 172 already read the same
 * field, so this was the one screen in the chain that dropped it.
 *
 * The destination goes through `safeInternalPath` and is not otherwise trusted. Router state is not
 * something this application authored, and a sign-in screen that redirects to whatever it is handed
 * is an open redirect: the customer signs in on the real domain and is then delivered somewhere
 * else, still believing they are inside the portal.
 */
export function LoginRoute() {
  const { signIn, status } = useSession();
  const gate = useMfaGate();
  const location = useLocation();

  // Null unless a guard put an internal path here, which is the only shape that survives the check.
  const intended = safeInternalPath((location.state as { from?: unknown } | null)?.from);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<SignInFailureKind | null>(null);

  // The widget, its token and its lifecycle now live in `app/auth/useTurnstile.ts`, because account
  // settings re-verifies the current password against the same `grant_type=password` endpoint this
  // form posts to and is therefore behind the same captcha. architecture.md section 4 already
  // settled that: "the authentication surfaces" is every surface that presents a password.
  const [turnstile, turnstileMount] = useTurnstile();

  if (status === "signed-in") {
    // The session exists; where it may go is not settled yet. Standing on the sign-in form while
    // that is decided is the honest state, and it lasts one round trip.
    if (gate.isLoading) {
      return (
        <AuthFrame brand>
          <BoxState kind="loading" title="Loggar in" />
        </AuthFrame>
      );
    }

    // A gate that could not be read is not a reason to admit the session anywhere. The verification
    // screen has a designed answer for its own failure; this screen does not.
    //
    // The destination travels on rather than being spent here. A session that owes a factor is not
    // admitted anywhere yet, and `VerificationRoute` reads the same field once the code is accepted,
    // so handing it over is what keeps the deep link alive across both steps instead of only one.
    if (gate.isError || (gate.data && gate.data.kind !== "satisfied")) {
      return (
        <Navigate
          to="/verify"
          replace
          state={intended ? { from: intended } : undefined}
        />
      );
    }

    /**
     * Where the person was going, and only otherwise the account's own front door.
     *
     * The fallback stays split by account, because the two kinds of account open onto different
     * places: a client onto the overview, an admin onto the request board. What changes is that an
     * intended destination now beats both, and it works for either kind, since an admin who was
     * going to `/admin/clients` and a client who was going to `/requests/REQ-142` are the same case
     * from here.
     */
    const fallback = gate.data?.adminRequired ? ADMIN_HOME : "/overview";

    return <Navigate to={intended ?? fallback} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setFailure(null);

    try {
      // No navigation here. The session lands, the gate answers, and the redirect above sends this
      // person to the one place their account actually opens onto. Deciding it here would mean
      // deciding it before the answer exists.
      await signIn(email, password, turnstile.token ?? undefined);
    } catch (error) {
      // The error is classified, never displayed. `classifySignInFailure` decides which of five
      // sentences this is, and every credential decision shares one of them.
      setFailure(classifySignInFailure(error));

      // The token is spent whether or not the sign-in succeeded, so the widget is asked for a new
      // one. Without this the second attempt fails on a reused token and reads as a second wrong
      // password.
      turnstile.reset();
    } finally {
      setPending(false);
    }
  }

  /**
   * Drawn after `loginPage` in `prototype/kund/pages-auth.js`, approved 2026-09-24: one box, the
   * wordmark above it, and on a computer the brand half beside it. The old brand panel's lead line
   * ("Följ ärenden, se webbplatsens status ...") and the eyebrow above the heading went with the
   * redraw: the wordmark is the eyebrow now, and the lead was a paragraph the third rule in design.md
   * section 3 takes away.
   */
  return (
    <AuthFrame brand titleId="login-heading">
      <h2 id="login-heading" className="k-auth-title">
        Logga in
      </h2>

      <form onSubmit={onSubmit} noValidate>
        <div className="box-field">
          <label htmlFor="email">E-postadress</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <PasswordField
          id="password"
          name="password"
          label="Lösenord"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          revealed={revealed}
          onToggle={() => setRevealed((value) => !value)}
        />

        {failure ? (
          <Callout tone="danger" role="alert" testId="login-failure">
            {signInFailureMessage(failure)}
          </Callout>
        ) : null}

        {turnstile.sitekey ? (
          <div ref={turnstileMount} className="cf-turnstile" data-testid="turnstile" />
        ) : null}

        {/*
          The button waits for the token, and that is a correctness property rather than
          politeness. Turnstile in managed mode takes a moment to answer, and a form submitted
          before it does sends no token at all. Supabase answers that with `captcha_failed`, the
          person sees a security-check message, and nothing they type can change it. Measured on
          staging on 2026-09-02: a submit five seconds after load produced exactly that.
        */}
        <button
          className="button is-primary is-block"
          type="submit"
          disabled={pending || turnstile.pending}
        >
          <span>
            {pending ? "Loggar in" : turnstile.pending ? "Säkerhetskontroll pågår" : "Öppna kundportalen"}
          </span>
        </button>
      </form>

      {/*
        The recovery link belongs on this screen and nowhere else. It is the only page a person
        who cannot sign in ever reaches, so a form they cannot navigate to is a form that does
        not exist.
      */}
      <div className="k-auth-links">
        <Link to="/recover">Glömt lösenordet?</Link>
        {/* The privacy information, 2026-10-06: open without signing in, so it is offered here too. */}
        <Link to="/privacy">Så hanterar jag dina uppgifter</Link>
        <p className="k-auth-note" data-testid="login-contact">
          Annan hjälp: mejla Petrovic Systems på <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>.
        </p>
      </div>
    </AuthFrame>
  );
}
